import { getInitials } from '../../lib/initials';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  getGroupMessages,
  sendGroupMessage,
  renameConversation,
  type GroupMessage,
  type GroupParticipant,
} from '../../services/api/messages';
import { MessageComposer } from './MessageComposer';
import { useAuth } from '../../hooks/useAuth';

function GroupAvatars({ participants }: { participants: GroupParticipant[] }) {
  const shown = participants.slice(0, 3);
  return (
    <div className="flex -space-x-2">
      {shown.map((p) => (
        <div key={p.id} className="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-200 flex items-center justify-center">
          {p.profileImageUrl ? (
            <img src={p.profileImageUrl} alt={p.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs font-semibold text-gray-500">{getInitials(p.name)}</span>
          )}
        </div>
      ))}
      {participants.length > 3 && (
        <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center">
          <span className="text-xs text-gray-600">+{participants.length - 3}</span>
        </div>
      )}
    </div>
  );
}

function GroupBubble({
  message,
  isMine,
  showSender,
}: {
  message: GroupMessage;
  isMine: boolean;
  showSender: boolean;
}) {
  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2`}>
      {!isMine && (
        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center mr-2 flex-shrink-0 self-end overflow-hidden">
          {message.sender.profileImageUrl ? (
            <img src={message.sender.profileImageUrl} alt={message.sender.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs font-semibold text-gray-500">{getInitials(message.sender.name)}</span>
          )}
        </div>
      )}
      <div className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
        {!isMine && showSender && (
          <span className="text-xs text-gray-500 mb-0.5 ml-1">{message.sender.name}</span>
        )}
        <div
          className={`px-4 py-2 rounded-2xl text-sm ${
            isMine
              ? 'bg-primary-green text-white rounded-br-sm'
              : 'bg-white text-gray-900 shadow-sm rounded-bl-sm'
          }`}
        >
          {message.content}
        </div>
        <span className="text-xs text-gray-400 mt-0.5 mx-1">
          {new Date(message.createdAt).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

export function GroupConversationView() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['group-messages', conversationId],
    queryFn: () => getGroupMessages(conversationId!),
    enabled: !!conversationId,
    refetchInterval: 5000,
  });

  const sendMutation = useMutation({
    mutationFn: (content: string) => sendGroupMessage(conversationId!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['group-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    },
  });

  const renameMutation = useMutation({
    mutationFn: (name: string | null) => renameConversation(conversationId!, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-messages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['group-conversations'] });
      setEditingName(false);
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages]);

  const startEditing = () => {
    const conv = data?.conversation;
    setNameInput(conv?.name ?? '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.select(), 0);
  };

  const commitRename = () => {
    const trimmed = nameInput.trim();
    renameMutation.mutate(trimmed || null);
  };

  if (!conversationId) {
    return <div className="flex items-center justify-center h-96 text-gray-500">Ingen samtale valgt</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-green" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-12 text-red-600">Kunne ikke indlæse beskeder</div>;
  }

  const conv = data?.conversation;
  const messages = data?.messages ?? [];
  const otherParticipants = conv?.participants.filter((p) => p.id !== user?.id) ?? [];
  const title = conv?.name || otherParticipants.map((p) => p.name).join(', ') || 'Gruppe';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b p-4 bg-white flex items-center gap-3 flex-shrink-0">
        <Link to="/messages" className="text-primary-green hover:text-secondary-green font-medium text-sm">
          ← Tilbage
        </Link>
        {conv && <GroupAvatars participants={conv.participants} />}
        <div className="flex-1 min-w-0">
          {editingName ? (
            <input
              ref={nameInputRef}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setEditingName(false);
              }}
              placeholder="Navn på samtalen..."
              className="w-full text-sm font-semibold text-gray-900 border-b border-primary-green outline-none bg-transparent"
              autoFocus
            />
          ) : (
            <button
              onClick={startEditing}
              className="font-semibold text-gray-900 truncate text-left w-full hover:text-primary-green transition-colors group flex items-center gap-1"
              title="Klik for at navngive samtalen"
            >
              <span className="truncate">{title}</span>
              <svg className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-green flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
          {conv && (
            <p className="text-xs text-gray-500 truncate">
              {conv.participants.map((p) => p.name).join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Start samtalen ved at sende en besked</p>
          </div>
        ) : (
          <>
            {messages.map((msg: GroupMessage, i: number) => {
              const prev = messages[i - 1];
              const showSender = !prev || prev.senderId !== msg.senderId;
              return (
                <GroupBubble
                  key={msg.id}
                  message={msg}
                  isMine={msg.senderId === user?.id}
                  showSender={showSender}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Composer */}
      <MessageComposer
        onSend={(content) => sendMutation.mutate(content)}
        disabled={sendMutation.isPending}
      />
    </div>
  );
}
